import { Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLineItemCustomFieldNames } from "@/store/quoteSettingsStore";
import {
  LINE_FIELD_OPTIONS,
  lineCustomFieldOptions,
  type LineFieldOption,
} from "./lineFields";

interface AddTableColumnSelectProps {
  /** Appends a column bound to the picked source, headed by its label. */
  onAdd: (source: string, header: string) => void;
}

/**
 * Appends a column to the selected item table, or to the table the selected
 * column belongs to. Custom fields lead, being the
 * ones an org reaches for most, and are grouped apart from the standard line
 * fields since a custom field can share a name with a standard one while the
 * two resolve from different places.
 * @param onAdd - Appends a column bound to the picked source
 * @returns The add-column picker
 */
export function AddTableColumnSelect({ onAdd }: AddTableColumnSelectProps) {
  const customFieldOptions = lineCustomFieldOptions(
    useLineItemCustomFieldNames(),
  );
  const bySource = new Map<string, LineFieldOption>(
    [...customFieldOptions, ...LINE_FIELD_OPTIONS].map((option) => [
      option.source,
      option,
    ]),
  );
  return (
    <Select
      value=""
      onValueChange={(source) => {
        const option = bySource.get(source);
        if (option) {
          onAdd(option.source, option.label);
        }
      }}
    >
      <SelectTrigger className="h-8 text-xs">
        {/* SelectTrigger line-clamps its direct span children, which forces
            display:-webkit-box and would stack the icon above the label; the
            inner span carries the row layout instead. */}
        <span>
          <span className="flex items-center gap-1 text-muted-foreground">
            <Plus className="size-3.5" /> Add column
          </span>
        </span>
        <SelectValue className="sr-only" />
      </SelectTrigger>
      <SelectContent>
        {customFieldOptions.length > 0 && (
          <>
            <SelectGroup>
              <SelectLabel>Custom fields</SelectLabel>
              {customFieldOptions.map((option) => (
                <SelectItem key={option.source} value={option.source}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
            <SelectSeparator />
          </>
        )}
        <SelectGroup>
          <SelectLabel>Line fields</SelectLabel>
          {LINE_FIELD_OPTIONS.map((option) => (
            <SelectItem key={option.source} value={option.source}>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
